// src/components/PaymentOptions.js
import React, { useState, useContext, useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import axios from 'axios'
import { Spinner } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import SubscriptionPage from './SubscriptionPage'
import PayPalSubscriptionButton from './PayPalSubscriptionButton'
import { UserContext } from '../context'
import { getUser, getAccessToken } from '../services/AuthService'
import { getCardBrandIcon } from '../services/CardValidationService'
import { showErrorNotification } from '../services/notificationService'

import './PaymentOptions.css'

/* ---------- UI helpers ---------- */
const Pill = ({ children, tone='blue' }) => (
  <span className={`pill pill--${tone}`}>{children}</span>
)

/* ---------- Plan card ---------- */
const PlanCard = ({ plan, selected, onSelect, isCurrent }) => {
  const yearly = plan.frequency_type?.toLowerCase() === 'year'

  // Build a safe image URL if the API returns a relative path
  const imageUrl =
    plan?.image
      ? (plan.image.startsWith('http')
          ? plan.image
          : `${process.env.REACT_APP_BASE_URL}${plan.image}`)
      : null

  return (
    <div
      className={`plan ${selected ? 'plan--selected' : ''} ${isCurrent ? 'plan--current' : ''} ${yearly ? 'plan--recommended' : ''}`}
      onClick={() => onSelect(plan)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(plan)}
    >
      <div className="plan__badge-row">
        {yearly && <Pill>Más Popular</Pill>}
        {isCurrent && <Pill tone="mint">Plan Actual</Pill>}
      </div>

      <div className="plan__media">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={plan?.name || 'Plan'}
            className="plan__image"
            loading="lazy"
          />
        ) : (
          <div className="plan__icon" aria-hidden>👑</div>
        )}
      </div>

      <h3 className="plan__title">{plan.name}</h3>

      <div className="plan__price">
        {plan.compare_at && <span className="plan__compare">${plan.compare_at}</span>}
        <span className="plan__now">${plan.price}</span>
        <span className="plan__suffix">/mes</span>
      </div>

      {!!plan.features?.length && (
        <ul className="plan__features">
          {plan.features.map((f, i) => (
            <li key={i} className="included">{f.name}</li>
          ))}
        </ul>
      )}

      <button className={`btn ${selected ? 'btn--ghost' : 'btn--primary'}`} type="button">
        {selected ? 'Plan seleccionado' : 'Elegir Plan'}
      </button>
    </div>
  )
}


/* ---------- Pago rápido con tarjeta guardada ---------- */
const DefaultPaymentBox = ({ defaultPaymentMethod, selectedPriceId, trialDays }) => {
  const [ , , actions] = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);
  const [ok, setOk] = useState(false);
  const [priceError, setPriceError] = useState('');

  const BASE = process.env.REACT_APP_BASE_URL;
  const authHeaders = () => ({ Authorization: `Bearer ${getAccessToken()}` });

  const normalizeTrial = () => {
    if (Array.isArray(trialDays) && trialDays.length > 0) {
      const v = Number(trialDays[0]?.days);
      return Number.isFinite(v) ? v : 0;
    }
    if (typeof trialDays === 'number') return trialDays;
    return 0;
  };

  
  const syncSubscription = async () => {
    await axios.post(`${BASE}/api/stripe/sync-subscription/`, {}, {
      headers: authHeaders(),
      timeout: 10000,
    });
  };

  const handleSubmit = async () => {
    if (!selectedPriceId) { setPriceError('Por favor selecciona un plan.'); return; }
    if (!defaultPaymentMethod?.id) { setPriceError('No hay método de pago guardado.'); return; }

    setIsLoading(true);
    try {
      const user = getUser();
      const trial = normalizeTrial();

      // Usa tu endpoint actual:
      await axios.post(
        `${BASE}/api/create_subscription/${user.id}/`,
        { payment_method_id: defaultPaymentMethod.id, price_id: selectedPriceId, trial },
        { headers: authHeaders(), timeout: 10000 }
      );

      await syncSubscription();
      await actions.refreshUser(); 

      setOk(true);
    } catch (e) {
      console.error(e);
      setOk(false);
      showErrorNotification(e);
    } finally {
      setSubmitted(true);
      setIsLoading(false);
    }
  };

  if (!defaultPaymentMethod) return <div className="muted">No hay métodos guardados.</div>;
  if (isSubmitted) return ok ? <Navigate to="/sub-success" /> : <Navigate to="/sub-cancel" />;

  return (
    <div className="pm-box">
      {priceError && <p className="text-danger">{priceError}</p>}
      <div className="payment-method-box payment-method-selected">
        <div className="payment-method-details">
          <FontAwesomeIcon icon={getCardBrandIcon(defaultPaymentMethod.card.brand)} />
          <p>Brand: {defaultPaymentMethod.card.brand.toUpperCase()}</p>
          <p>Last 4: **** **** **** {defaultPaymentMethod.card.last4}</p>
          <p>Expira: {defaultPaymentMethod.card.exp_month}/{defaultPaymentMethod.card.exp_year}</p>
        </div>
      </div>
      <button className="btn btn--primary" onClick={handleSubmit} disabled={isLoading}>
        {isLoading ? 'Procesando…' : 'Pagar con esta tarjeta'}
      </button>
    </div>
  )
}

/* ---------- Drawer azul con tabs ---------- */
const PaymentDrawer = ({
  open, selectedPlan, selectedPriceId, paypalPlanId, trialDays,
  defaultPaymentMethod, loadingPM
}) => {
  const [tab, setTab] = useState('card') // 'card' | 'paypal'
  const bodyRef = useRef(null)

  useEffect(() => {
    if (open && bodyRef.current) {
      bodyRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [open])

  return (
    <div className={`drawer ${open ? 'drawer--open' : 'drawer--closed'}`} ref={bodyRef}>
      <div className="drawer__head">
        <div className="drawer__icon">💳</div>
        <div className="drawer__title">
          {selectedPlan ? `Pagar ${selectedPlan.name}` : 'Selecciona un plan'}
          {selectedPlan && <span className="drawer__price"> ${selectedPlan.price}/mes</span>}
        </div>
      </div>

      <div className="drawer__body">
        {/* Tabs */}
        <div className="segmented">
          <button
            className={`segmented__btn ${tab === 'card' ? 'is-active' : ''}`}
            onClick={() => setTab('card')}
          >
            Tarjeta
          </button>
          <button
            className={`segmented__btn ${tab === 'paypal' ? 'is-active' : ''}`}
            onClick={() => setTab('paypal')}
          >
            PayPal
          </button>
        </div>

        {/* Contenido */}
        {tab === 'card' ? (
          <>
            <div className="section">
              <div className="section__title">Tarjeta guardada</div>
              {loadingPM
                ? <div className="muted"><Spinner animation="border" size="sm" /> Cargando método…</div>
                : <DefaultPaymentBox
                    defaultPaymentMethod={defaultPaymentMethod}
                    selectedPriceId={selectedPriceId}
                    trialDays={trialDays}
                  />
              }
            </div>

            <div className="divider">o usar nueva tarjeta</div>

            <div className="section">
              <div className="section__title">Nueva tarjeta</div>
              <SubscriptionPage
                 isLoggedIn={true}
                 selectedPriceId={selectedPriceId}
                 trialDays={trialDays}
                 selectDefaultPaymentMethod={null}
               />
            </div>
          </>
        ) : (
          <div className="section">
            <div className="section__title">Pagar con PayPal</div>
            <PayPalSubscriptionButton plan_id={paypalPlanId} />
          </div>
        )}
      </div>
    </div>
  )
}

/* ---------- Página principal ---------- */
const PaymentOptions = ({ isLoggedIn, trialDays = [] }) => {
  const [state] = useContext(UserContext)

  // planes
  const [plans, setPlans] = useState([])
  const [loadingPlans, setLoadingPlans] = useState(true)
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [selectedPriceId, setSelectedPriceId] = useState(null)
  const [paypalPlanId, setPaypalPlanId] = useState(null)

  // método de pago por defecto
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null)
  const [loadingPM, setLoadingPM] = useState(false)

  // 1) Cargar planes al montar
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/subscription_plan/`,
          { timeout: 5000 }
        )
        setPlans(data || [])
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingPlans(false)
      }
    }
    fetchPlans()
  }, [])

  // 2) Cargar método guardado SOLO tras elegir plan (y solo si hay customer)
  const fetchPM = async () => {
    const user = getUser()
    if (!user) return

    // si en tu estado ya sabes que no existe customer, evita la llamada
    const hasCustomer = state?.user?.stripe_customer_id
    if (!hasCustomer) {
      setDefaultPaymentMethod(null)
      return
    }

    setLoadingPM(true)
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/payment_method/${user.id}/`,
        { headers: { Authorization: `Bearer ${getAccessToken()}` }, timeout: 5000 }
      )
      setDefaultPaymentMethod(data?.default_payment_method || null)
    } catch (e) {
      const status = e?.response?.status
      // si backend dice que no hay customer o recurso -> ignora (no toast)
      if (status === 400 || status === 404) {
        console.warn('Sin customer en Stripe todavía; ignorando.')
        setDefaultPaymentMethod(null)
      } else {
        // otros errores sí los notificas
        showErrorNotification(e)
      }
    } finally {
      setLoadingPM(false)
    }
  }


  const onSelectPlan = (plan) => {
    setSelectedPlan(plan)
    setSelectedPriceId(plan.stripe_price_id)
    setPaypalPlanId(plan.paypal_plan_id)
    fetchPM() // <- Opción A: recién aquí pedimos el PM guardado
  }

  if (!isLoggedIn) return <Navigate to="/log-in" />

  return (
    <div className="checkout">
      {/* 1) Planes primero */}
      <div className="sheet">
        <div className="sheet__title">Elige tu plan</div>
        {loadingPlans ? (
          <div className="muted"><Spinner animation="border" size="sm" /> Cargando planes…</div>
        ) : (
          <div className="plan-grid">
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                isCurrent={state?.user?.active && p.stripe_price_id === state?.user?.stripe_price_id}
                selected={selectedPlan?.stripe_price_id === p.stripe_price_id}
                onSelect={onSelectPlan}
              />
            ))}
          </div>
        )}
      </div>

      {/* 2) Drawer de pago (aparece al elegir un plan) */}
      <PaymentDrawer
        open={!!selectedPlan}
        selectedPlan={selectedPlan}
        selectedPriceId={selectedPriceId}
        paypalPlanId={paypalPlanId}
        trialDays={trialDays}
        defaultPaymentMethod={defaultPaymentMethod}
        loadingPM={loadingPM}
      />
    </div>
  )
}

export default PaymentOptions
